/**
 * Toast — 전역 알림 채널 (Provider + useToast 훅)
 * ----------------------------------------------------------------------------
 * 보고서 §6: 통합 알림 채널 부재. Phase 5 의 silent catch 제거 시 에러 노출 통로.
 * 이번 Phase 에서는 Provider + 훅 + 컴포넌트만 제공한다 (silent catch 교체는 Phase 5).
 *
 * 설치: App 최상단을 <ToastProvider> 로 감싼다.
 * 사용:  const toast = useToast(); toast.show({ type: "error", message: "저장 실패" });
 */
import React from "react";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";
import { useLanguage } from "./LanguageProvider";

export type ToastType = "success" | "error" | "info";

type ToastAction = { label: string; onClick: () => void };
type ToastItem = { id: number; type: ToastType; message: string; action?: ToastAction };

type ToastApi = {
  /** action — 되돌릴 수 있는 변경이면 "실행 취소" 같은 버튼을 단다(누르면 실행하고 닫힘). 확인창 대신 쓴다. */
  show: (t: { type?: ToastType; message: string; duration?: number; action?: ToastAction }) => void;
};

const ToastContext = React.createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = React.useContext(ToastContext);
  if (!ctx) {
    // Provider 미설치 시 콘솔 폴백 (크래시 방지)
    return {
      show: ({ message }) => {
        // eslint-disable-next-line no-console
        console.warn("[toast]", message);
      },
    };
  }
  return ctx;
}

const COLOR: Record<ToastType, { fg: string; soft: string; border: string; icon: React.ReactNode }> = {
  success: { fg: "var(--success)", soft: "var(--success-soft)", border: "var(--success-border)", icon: <CheckCircle2 size={16} /> },
  error: { fg: "var(--danger)", soft: "var(--danger-soft)", border: "var(--danger-border)", icon: <AlertCircle size={16} /> },
  info: { fg: "var(--info)", soft: "var(--info-soft)", border: "var(--info-border)", icon: <Info size={16} /> },
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const { t: tr } = useLanguage();
  const [items, setItems] = React.useState<ToastItem[]>([]);
  const idRef = React.useRef(0);

  const remove = React.useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = React.useCallback<ToastApi["show"]>(
    ({ type = "info", message, duration, action }) => {
      const id = ++idRef.current;
      // 실행 취소가 붙은 토스트는 누를 시간을 더 준다(기본 6초)
      duration = duration ?? (action ? 6000 : 4000);
      setItems((prev) => [...prev, { id, type, message, action }]);
      if (duration > 0) {
        window.setTimeout(() => remove(id), duration);
      }
    },
    [remove]
  );

  const api = React.useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        style={{
          position: "fixed",
          top: "var(--space-5)",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "var(--space-2)",
          zIndex: "var(--z-toast)" as unknown as number,
          pointerEvents: "none",
        }}
      >
        {/* 들어올 때 살짝 튀며 내려옴(동작 줄이기면 끔) */}
        <style>{"@keyframes vault-toast-in{from{opacity:0;transform:translateY(-10px) scale(.97)}}.vault-toast{animation:vault-toast-in .26s cubic-bezier(.34,1.56,.64,1)}@media (prefers-reduced-motion:reduce){.vault-toast{animation:none}}"}</style>
        {items.map((t) => {
          const c = COLOR[t.type];
          return (
            <div
              key={t.id}
              role="status"
              className="vault-toast"
              style={{
                pointerEvents: "auto",
                display: "flex",
                alignItems: "center",
                gap: "var(--space-2)",
                minWidth: 240,
                maxWidth: 380,
                padding: "var(--space-3) var(--space-4)",
                background: "var(--surface-inverse)",
                border: `1px solid ${c.border}`,
                borderLeft: `3px solid ${c.fg}`,
                borderRadius: "var(--radius-md)",
                boxShadow: "var(--shadow-md)",
                color: "var(--text-inverse)",
                fontSize: "var(--text-sm)",
              }}
            >
              <span style={{ color: c.fg, display: "flex", flexShrink: 0 }}>{c.icon}</span>
              <span style={{ flex: 1, lineHeight: "var(--leading-normal)" }}>{t.message}</span>
              {t.action ? (
                <button
                  type="button"
                  onClick={() => { t.action?.onClick(); remove(t.id); }}
                  style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--accent, #9cceff)", fontWeight: 700, fontSize: "var(--text-sm)", padding: "0 4px", minHeight: 28, flexShrink: 0 }}
                >
                  {t.action.label}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => remove(t.id)}
                aria-label={tr("닫기", "Close")}
                // 누르는 목표 28px(피츠 R1) — 아이콘은 14px 그대로, 바깥 여백을 음수로 둬 자리는 안 바뀐다
                style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, width: 28, height: 28, margin: "-7px -7px -7px 0", flexShrink: 0 }}
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
